// Leaf module: the default LineItemsState (and its constituent sub-defaults) used as the initial
// reducer state and as the fallback value on LineItemsContext. Kept separate from ./actions so
// LineItemsContext can consume real defaults without transitively importing the reducer/action
// handlers.
//
// Leaf-only imports: types + a DEFAULT_CURRENCY constant. Do NOT import from ./actions,
// ./LineItemsProvider, ./LineItemsReducer, or any renderer module.

import { DEFAULT_CURRENCY } from '@utils/currency';

import {
  BaselineState,
  ErrorState,
  EventState,
  ID,
  ImportState,
  LineItemsState,
} from 'types';

export const defaultImportState: ImportState = {
  data: {
    deletedQualifyingCustomColumnHeaders: [],
    errorLotIndexes: [],
    importedLots: [],
    lotErrorMap: {},
    totalExistingLineItems: 0,
    totalImportedLineItems: 0,
  },
  status: {
    disableSave: false,
    imported: false,
    inProgress: false,
    saveLineItemsAfterImport: false,
    showDescriptiveColumnsWarningModal: false,
    showLineItemsModal: false,
    showOneSchemaImportModal: false,
    uploadStartedFromEmptyLot: false,
    uploadStartedFromNewEvent: false,
  },
};

export const defaultEventState: EventState = {
  collectItemsPerUnit: false,
  currency: DEFAULT_CURRENCY,
  hasInvitedOrScheduledSuppliers: false,
  hasSuppliers: false,
  isReadOnly: false,
  originalCurrency: DEFAULT_CURRENCY,
  paginationEnabled: false,
  setBaselineAverage: false, // on first round quotes
  showDiscount: true,
  showExtendedPriceFeedback: true,
  showLogisticsCharge: false,
  showPricingColumns: true,
  supplierOrganizations: [],
};

export const defaultBaselinePriceState: BaselineState = {
  baselineCount: 0,
  baselinesApplied: false,
  fetchBaselines: false,
  fetchBaselinesAfterSave: false,
  inProgress: false,
  showBaselinePriceUpdateAlert: false,
  zeroPricedLineItemsCount: 0,
};

export const defaultLineItemsState: LineItemsState = {
  baselineState: defaultBaselinePriceState,
  changes: {},
  event: defaultEventState,
  importState: defaultImportState,
  isInProgress: false,
  isLoading: false,
  lots: undefined, // Loading state
  lotsErrors: {} as Record<ID, ErrorState>,
  lotsPagination: {},
  originalLots: [],
  refreshValidation: false,
  shellRefreshCount: 0,
  shouldPreserveNewLotsOnRefresh: true,
  shouldRefresh: true,
  shouldRerenderLineItemData: false,
};
